import { FC, useEffect, useState, useCallback } from 'react'
import PortalLearningPaths from '@classes/LearningPaths'
import Loader from '@layouts/LearningPath/Layout/Loader'
import { LPContextProvider } from '@layouts/LearningPath/Layout/Context/LPContext'
import { ScormPanel, Complete, InProgress, Start } from '../components'
import { DEFAULT_LANG, getLanguageLabel, getUnitStatus, hasPublishedScormUnits } from '../helpers'
import { LP_STATUS, LP_UNIT_TYPE, SCORM_STATUS } from '@utils/constants/learningPath'

interface PdContentProps {
  LearningPaths: PortalLearningPaths
  onRedirectToMain?: () => void
  currentLanguage: string
  onLanguageChange: (lang: string) => void
}

const PdContent: FC<PdContentProps> = ({ LearningPaths, onRedirectToMain, currentLanguage, onLanguageChange }) => {
  const hasScorm = !!hasPublishedScormUnits(LearningPaths)

  useEffect(() => {
    if (!hasScorm) onRedirectToMain?.()
  }, [hasScorm, onRedirectToMain])

  if (!hasScorm) return null

  const allPathUnits = LearningPaths?.learningPaths?.flatMap((lp: any) => lp?.details?.units || []) || []

  const availableLanguages = [
    ...new Set(
      allPathUnits
        .filter((unit: any) => unit.unittype === LP_UNIT_TYPE.SCORM)
        .flatMap((unit: any) =>
          (unit.packages || []).filter((pkg: any) => pkg.published && pkg.lang).map((pkg: any) => pkg.lang)
        )
    ),
  ] as string[]

  const languageDropdownOptions = availableLanguages.map((lang) => ({
    key: lang,
    value: lang,
    label: getLanguageLabel(lang),
  }))

  const renderUnitCard = (unit: any) => {
    const status = getUnitStatus(unit?.packages)
    switch (status) {
      case LP_STATUS.NOT_YET_STARTED:
        return <Start unit={unit} />
      case LP_STATUS.IN_PROGRESS:
        return <InProgress unit={unit} />
      case LP_STATUS.COMPLETE:
        return <Complete unit={unit} />
      default:
        return null
    }
  }

  const scormUnits = allPathUnits
    .map((unit: any) => {
      if (unit.unittype !== LP_UNIT_TYPE.SCORM) return null
      const localizedPackage = (unit.packages || []).find((pkg: any) => {
        if (!pkg.published) return false
        const pkgLang = pkg.lang ? pkg.lang : DEFAULT_LANG
        return pkgLang === currentLanguage
      })
      if (!localizedPackage) return null
      return { ...unit, packages: [localizedPackage] }
    })
    .filter(Boolean)
    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))

  return (
    <LPContextProvider>
      <ScormPanel
        scormUnits={scormUnits}
        renderUnitCard={renderUnitCard}
        hasContent={scormUnits.length > 0}
        languageOptions={languageDropdownOptions}
        currentLanguage={currentLanguage}
        onLanguageChange={onLanguageChange}
      />
    </LPContextProvider>
  )
}

export interface ProfessionalDevelopmentTabProps {
  loading: boolean
  LearningPaths: PortalLearningPaths
  onRedirectToMain?: () => void
}

const ProfessionalDevelopmentTab: FC<ProfessionalDevelopmentTabProps> = ({
  loading,
  LearningPaths,
  onRedirectToMain,
}) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('pdLanguage') || '')
  const [languageResolved, setLanguageResolved] = useState(false)
  const handleLanguageChange = useCallback((newLang: string) => setCurrentLanguage(newLang), [])

  useEffect(() => {
    if (loading) return

    if (currentLanguage) {
      setLanguageResolved(true)
      return
    }

    const allPathUnits = LearningPaths?.learningPaths?.flatMap((lp: any) => lp?.details?.units || []) || []

    const inProgressLangs = [
      ...new Set(
        allPathUnits
          .filter((unit: any) => unit.unittype === LP_UNIT_TYPE.SCORM)
          .flatMap((unit: any) =>
            (unit.packages || [])
              .filter((pkg: any) => pkg.published && pkg.status === SCORM_STATUS.IN_PROGRESS && pkg.lang)
              .map((pkg: any) => pkg.lang)
          )
      ),
    ] as string[]

    if (inProgressLangs.length === 1) {
      localStorage.setItem('pdLanguage', inProgressLangs[0])
      setCurrentLanguage(inProgressLangs[0])
      setLanguageResolved(true)
      return
    }

    setLanguageResolved(true)
  }, [loading, LearningPaths, currentLanguage])

  if (loading || !languageResolved) return <Loader />

  return (
    <PdContent
      LearningPaths={LearningPaths}
      onRedirectToMain={onRedirectToMain}
      currentLanguage={currentLanguage}
      onLanguageChange={handleLanguageChange}
    />
  )
}

export default ProfessionalDevelopmentTab
